/**
 * Public platform-connection page — data contract.
 *
 * The server is the author of every claim on this page: the platform labels,
 * the access classifications, the state vocabulary, the blockers and the
 * progress counts all arrive already decided. This module reduces the response
 * to that contract before anything reaches the DOM, so a field the contract
 * does not name cannot be rendered even if the server grows one.
 *
 * It formats; it never authors a status.
 */

export const CONNECT_STATUS_POLL_INTERVAL_MS = 15_000;

/**
 * The eleven states the backend can report. The tone drives colour only — the
 * words always come from the server's own `stateLabel`.
 */
export const CONNECTION_STATE_TONES = Object.freeze({
  not_connected: "idle",
  connection_in_progress: "pending",
  authorization_returned: "pending",
  account_selection_required: "action",
  connected: "positive",
  reauthorization_required: "attention",
  insufficient_permissions: "attention",
  setup_required: "attention",
  temporarily_unavailable: "blocked",
  policy_disabled: "blocked",
  connection_failed: "attention",
});

/** States where offering a Connect button would be a lie. */
const NON_CONNECTABLE_STATES = new Set([
  "temporarily_unavailable",
  "policy_disabled",
  "setup_required",
]);

export const CONNECTED_STATE = "connected";
export const FAILED_STATE = "connection_failed";

/**
 * Recovery copy for the coarse reason codes the callback bridge appends. The
 * provider's own message is deliberately never sent to the browser, so these
 * are the only failure words the page can show.
 */
export const RETURN_REASON_COPY = Object.freeze({
  authorization_denied:
    "The authorization was cancelled before it finished. Nothing changed — you can start it again whenever you are ready.",
  state_expired:
    "That connection attempt took too long and expired. Starting it again takes a few seconds.",
  missing_scope:
    "The platform did not grant every permission Cphere asked for. Start the connection again and accept each permission on the platform's screen.",
  no_eligible_account:
    "The authorization worked, but Cphere could not find an account it can use. Check that the profile you signed in with has access to the right account, then try again.",
  persist_failed:
    "The platform authorized Cphere, but Cphere could not save the connection. Please try once more — your Cphere contact has been given the detail.",
  provider_error:
    "The platform could not complete the authorization. Please try again, and tell your Cphere contact if it keeps happening.",
});

export const GENERIC_RETURN_FAILURE_COPY = RETURN_REASON_COPY.provider_error;

function text(value) {
  return typeof value === "string" ? value.trim() : "";
}

function bool(value) {
  return value === true;
}

function nullableBool(value) {
  return value === true || value === false ? value : null;
}

function integer(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.trunc(parsed) : 0;
}

function stringList(value) {
  return Array.isArray(value) ? value.map(text).filter(Boolean) : [];
}

/** One platform card. Allowlisted, field by field. */
function sanitizePlatformCard(entry) {
  const platform = entry?.platform || {};
  const state = text(entry?.state) || "not_connected";
  return {
    id: text(platform.id),
    heroKey: text(platform.heroKey) || text(platform.id),
    label: text(platform.label),
    officialName: text(platform.officialName),
    enables: text(platform.enables),
    purpose: text(platform.purpose),
    accessClass: text(platform.accessClass),
    accessClassLabel: text(platform.accessClassLabel),
    scopeSummary: text(platform.scopeSummary),
    accountResourceNoun: text(platform.accountResourceNoun),
    accountSelectionRequired: bool(platform.accountSelectionRequired),
    requiresShopDomain: bool(platform.requiresShopDomain),
    // How Cphere obtains this platform's access. The ONLY kind an advertiser
    // can act on is an OAuth flow they complete on the platform's own site.
    authorizationKind: text(platform.authorizationKind),

    state,
    stateLabel: text(entry?.stateLabel),
    tone: CONNECTION_STATE_TONES[state] || "idle",
    blocker: text(entry?.blocker),
    required: bool(entry?.required),
    healthy: bool(entry?.healthy),
    canConnect: bool(entry?.canConnect),

    facts: {
      oauthAuthorized: bool(entry?.facts?.oauthAuthorized),
      scopesGranted: nullableBool(entry?.facts?.scopesGranted),
      accountsDiscovered: nullableBool(entry?.facts?.accountsDiscovered),
      accountSelected: bool(entry?.facts?.accountSelected),
      mappingConfirmed: bool(entry?.facts?.mappingConfirmed),
      reportingReady: bool(entry?.facts?.reportingReady),
      writePermitted: bool(entry?.facts?.writePermitted),
    },
    selectedAccount: entry?.selectedAccount
      ? { id: text(entry.selectedAccount.id), name: text(entry.selectedAccount.name) }
      : null,
    lastVerifiedAt: text(entry?.lastVerifiedAt) || null,
  };
}

/**
 * The kind of authorization an advertiser can actually perform.
 *
 * Cphere obtains access three ways, and only one of them is the advertiser's
 * to give: `oauth` is a flow the advertiser completes on the platform's own
 * site; `api_key` is a rail Cphere provisions with a Cphere-held key (there
 * is nothing to authorize, and StackAdapt is the one that behaves this way);
 * `none` is a platform with no rail at all.
 *
 * A card that is not `oauth` therefore has no honest action, so it is not a
 * card. It is dropped here rather than rendered disabled, captioned "handled
 * by Cphere", or explained — a client reading this page should see only the
 * things they are being asked to do.
 */
const CLIENT_AUTHORIZED_KIND = "oauth";

/** Can this provider take an authorization from the advertiser reading the page? */
export function isClientAuthorizedPlatform(card) {
  return text(card?.authorizationKind) === CLIENT_AUTHORIZED_KIND;
}

/** The whole page payload, reduced to the contract. */
export function sanitizePublicOnboarding(payload) {
  if (!payload || typeof payload !== "object") return null;
  const platforms = Array.isArray(payload.platforms)
    ? payload.platforms
        .map(sanitizePlatformCard)
        .filter((card) => card.id)
        // Issuance already refuses a non-OAuth provider, so this is the second
        // line rather than the first — it holds for a legacy invitation issued
        // before that refusal, and for anything a future payload adds.
        .filter(isClientAuthorizedPlatform)
    : [];
  const progress = payload.progress || {};
  return {
    advertiserName: text(payload.advertiser?.name),
    // Absolute https only — the server drops anything else. The page always
    // has the advertiser's name to fall back to.
    advertiserLogoUrl: text(payload.advertiser?.logoUrl) || null,
    advertiserLogoAlt: text(payload.advertiser?.logoAlt) || null,
    // Absent for a legacy advertiser-only link, which is a real state and not
    // a campaign whose name failed to load.
    campaignName: text(payload.campaign?.name) || null,
    requestedByName: text(payload.requestedBy?.name),
    requestedByCompany: text(payload.requestedBy?.company) || "Cphere",
    note: text(payload.note),
    linkStatus: text(payload.linkStatus) || "active",
    linkTitle: text(payload.title),
    linkBody: text(payload.body),
    expiresAt: text(payload.expiresAt) || null,
    completedAt: text(payload.completedAt) || null,
    platforms,
    progress: {
      connectedCount: integer(progress.connectedCount),
      totalCount: integer(progress.totalCount),
      requiredConnectedCount: integer(progress.requiredConnectedCount),
      requiredTotalCount: integer(progress.requiredTotalCount),
      outstandingRequired: stringList(progress.outstandingRequired),
      skippedOptional: stringList(progress.skippedOptional),
      complete: bool(progress.complete),
    },
  };
}

/** "3 of 5 platforms connected" — the count is the server's, not a tally here. */
export function formatProgressLine({ connectedCount = 0, totalCount = 0 } = {}) {
  const noun = totalCount === 1 ? "platform" : "platforms";
  return `${connectedCount} of ${totalCount} ${noun} connected`;
}

/** 0–100, for the progress meter. Never exceeds the total. */
export function progressPercent({ connectedCount = 0, totalCount = 0 } = {}) {
  if (!totalCount) return 0;
  return Math.min(100, Math.max(0, Math.round((connectedCount / totalCount) * 100)));
}

/** A Connect action is offered only where the backend says one can succeed. */
export function connectActionFor(card) {
  if (!card || !card.canConnect || NON_CONNECTABLE_STATES.has(card.state)) return null;
  if (card.state === CONNECTED_STATE) return { kind: "reconnect", label: "Reconnect" };
  if (card.state === "account_selection_required" && card.accountSelectionRequired) {
    return { kind: "choose", label: `Choose ${card.accountResourceNoun || "an account"}` };
  }
  if (card.state === "connection_in_progress") return { kind: "resume", label: "Continue" };
  // A failed attempt is a retry, not a first connection. The card already says
  // "Connection failed"; the action names the platform so the button still
  // reads unambiguously on a page carrying several of them.
  if (card.state === FAILED_STATE) return { kind: "connect", label: `Retry ${card.label}` };
  if (card.facts?.oauthAuthorized) return { kind: "connect", label: "Authorize again" };
  return { kind: "connect", label: `Connect ${card.label}` };
}

/** The seven facts, in the order they actually happen. */
export function factRows(card) {
  if (!card) return [];
  const yes = (value) => (value === null ? "Not recorded" : value ? "Yes" : "No");
  return [
    { key: "oauthAuthorized", label: "Authorized with platform", value: yes(card.facts.oauthAuthorized) },
    { key: "scopesGranted", label: "Requested permissions granted", value: yes(card.facts.scopesGranted) },
    { key: "accountSelected", label: "Account selected", value: yes(card.facts.accountSelected) },
    { key: "mappingConfirmed", label: "Linked to this advertiser", value: yes(card.facts.mappingConfirmed) },
    { key: "reportingReady", label: "Reporting ready", value: yes(card.facts.reportingReady) },
  ];
}

/** Human date for the footer. Returns "" rather than an invalid-date string. */
export function formatTimestamp(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/** Reads the callback bridge's outcome off the URL. Nothing else is trusted. */
export function readReturnOutcome(search) {
  const params = new URLSearchParams(String(search || ""));
  const connection = text(params.get("connection"));
  if (!connection) return null;
  const platform = text(params.get("platform"));
  if (connection === "success") return { platform, ok: true, copy: null };
  const reason = text(params.get("reason"));
  return {
    platform,
    ok: false,
    reason,
    copy: RETURN_REASON_COPY[reason] || GENERIC_RETURN_FAILURE_COPY,
  };
}
